import {
  AfterViewInit,
  Component,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {BucketUnitEnum, ChartTypesEnum} from "../../enums/dashboard/ChartsEnum";
import {VisualRange} from "devextreme/common/charts";
import {BaseComponentWithDrawer} from "../registry/base-component-with-drawer.component";
import {EditChartEvent} from "../../events/EditChartEvent";
import {DashboardService} from "../../services/dashboard/dashboard.service";
import {CreateMetricGroupEvent} from "../../events/CreateMetricGroupEvent";
import {ReloadDataEvent} from "../../events/ReloadDataEvent";
import {TelemetryOpeService} from "../../services/ope/telemetryOpe.service";
import {GetTimeChartMetricsCB, TimeChartRealMetricDTO} from "../../ope/telemetry/GetTimeChartMetricsCB";
import {UserOpeService} from "../../services/ope/userOpe.service";
import {GetAllUserMetricGroupCB} from "../../ope/user/metric/GetAllUserMetricGroupCB";
import {ZoomEndEvent} from "devextreme/viz/chart";
import {DxButtonComponent, DxChartComponent, DxDrawerComponent, DxRangeSelectorComponent} from "devextreme-angular";
import {TypeOfDateSettingsEnumCharts} from "../../enums/dashboard/TypeOfDateSettingsEnumCharts";
import {TimeUnitEnum} from "../../enums/dashboard/TimeUnitEnum";
import {DomSanitizer} from "@angular/platform-browser";
import {ExportChartEvent} from "../../events/ExportChartEvent";
import {PutUserMetricGroupCB} from "../../ope/user/metric/PutUserMetricGroupCB";
import {UserMetricGroup} from "../../models/user/Metric/UserMetricGroup";
import {DelUserMetricGroupCB} from "../../ope/user/metric/DelUserMetricGroupCB";
import {DeleteChartEvent} from "../../events/DeleteChartEvent";
import {ChangeImpersonatedUserEvent} from "../../events/ChangeImpersonatedUserEvent";
import {ChartFormEditComponent} from "./chart-form/chart-form-drawer/chart-form-edit.component";
import {DataTypeGroupEnum} from "../../enums/dashboard/DataTypeUnitEnum";
import {UserMetricInstance} from "../../models/user/Metric/UserMetricInstance";
import {DatePipe} from "@angular/common";

@Component({
  selector: 'app-charts',
  templateUrl: './charts.component.html',
  styleUrls: ['./charts.component.css', '../common/drawer-style.css', './chart-form/chart-form-drawer/chart-form-edit.component.css'],
})
export class ChartsComponent extends BaseComponentWithDrawer implements OnInit, AfterViewInit, OnChanges, OnDestroy {

  /**
   * Reference to the chart HTML component
   */
  @ViewChild('chartComponent', {static: false}) chartComponent?: DxChartComponent;

  /**
   * Reference to the chart zoom slider HTML component
   */
  @ViewChild('chartSliderComponent', {static: false}) chartSliderComponent?: DxRangeSelectorComponent;

  /**
   * Reference to the reset zoom FAB button HTML component
   */
  @ViewChild('resetZoomButton', {static: false}) resetZoomButton?: DxButtonComponent;

  /**
   * Reference to the reset zoom FAB button HTML component
   */
  @ViewChild('formDrawerComponent', {static: false}) formDrawerComponent?: ChartFormEditComponent;
  @ViewChild('drawerComponent', {static: false}) drawerComponent?: DxDrawerComponent;

  /**
   * Contains the selected user metric group if the chart is viewed as a widget. If it is used as a fullscreen chart it uses the selUserMetricGroup in the dashboardService.
   */
  @Input('userMetricGroup') userMetricGroup?: UserMetricGroup;

  /**
   * Flag signaling if this component is widget or fullscreen
   * - False = Full screen
   * - True = Widget
   */
  @Input('isWidget') isWidget: boolean = false;
  createRequestSent: boolean = false;
  /**
   * True if chart is zoomed on the X axis
   */
  protected chartIsZoomedX = false;
  /**
   * True if chart is zoomed on the Y axis
   */
  protected chartIsZoomedY = false;
  /**
   * Controls if the popup is shown or closed, when false the popup form component is destroyed
   * @protected
   */
  protected isPopupOpen = false;
  /**
   * Flag containing information about whether the chart data is being loaded or it is ready
   * @protected
   */
  protected loadingChartInfo = true;
  /**
   * Visualized chart range on the X axis
   * @protected
   */

  protected visualRangeX!: VisualRange;
  /**
   * Original visual range of the chart on the Y axis
   * @protected
   */
  protected originalRangeY: VisualRange = {};
  /**
   * Array containing all the data to be shown in the chart.
   * It is a Map which contains:
   * - 'ts' field: the position on the X axis of a certain point
   * - one or more uuids (representing the unique metric alias): the value that the metric has at that point in time
   * @protected
   */
  protected chartData!: Object[];
  /**
   * Flag that controls whether to synchronize the value axis. This means unifying them in one shared scale rather than
   * dividing it for each measurement unit
   * @protected
   */
  protected standardizeValue = true;
  /**
   * List of the value axis, one for each measurement unit
   * @protected
   */
  protected valueAxisList: string[];
  protected hiddenAxes = new Set<string>();
  /**
   * Map used by the html to parse the chart type values in values accepted by the dx chart component.
   * Its values are initialized in the constructor
   * @protected
   */
  protected readonly chartTypesEnumWrapper: any;
  /**
   * Service containing the shared data and procedures among dashboard components
   * @protected
   */
  protected dashboardService = inject(DashboardService);
  /**
   * Service used for api calls related to users
   * @protected
   */
  protected userService = inject(UserOpeService);
  /**
   * Service used for api calls related to telemetries
   * @protected
   */
  protected telemetryService = inject(TelemetryOpeService);
  /**
   * Service used for api calls related to users
   * @protected
   */
  protected userOpeService = inject(UserOpeService);
  /**
   * Service used to sanitized composed HTML in order to display it on the page
   * @private
   */
  protected sanitizer: DomSanitizer = inject(DomSanitizer);
  /**
   * Pipe used to translate timestamps in string dates
   * @protected
   */
  protected datePipe: DatePipe = inject(DatePipe);
  /**
   * Variable in which we save the polling enabled
   */
  protected chartInterval?: any;
  protected readonly DataTypeGroupEnum = DataTypeGroupEnum;

  protected cssColorLabel!: string;
  protected cssColorMainBg!: string;
  protected cssColorPrimary!: string;
  private _tooltipCache: { arg: number; html: any } | null = null;

  protected readonly tooltipShadow = { blur: 4, color: 'black', offsetX: 0, offsetY: 2, opacity: 0.2 };
  protected readonly argAxisLabel = { overlappingBehavior: 'hide' as const };
  protected readonly emptyArray: any[] = [];
  protected argAxisTick!: object;
  protected dragBoxStyle!: object;
  protected booleanAxisUnit!: string;
  protected booleanAxisCategories!: string[];

  constructor() {
    // Super class constructor call
    super();
    this.valueAxisList = [];
    this.chartTypesEnumWrapper = {};
    this.userMetricGroup = new UserMetricGroup();
    Object.entries(ChartTypesEnum).forEach(cte => {
      this.chartTypesEnumWrapper[cte[0]] = cte[1];
    });
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.cssColorLabel = this.getStylizedCSSVariable('--dx-texteditor-color-label');
    this.cssColorMainBg = this.getStylizedCSSVariable('--dx-color-main-bg');
    this.cssColorPrimary = this.getStylizedCSSVariable('--dx-color-primary');
    this.dragBoxStyle = { color: this.cssColorPrimary };
    this.argAxisTick = { visible: !this.isWidget };
    this.booleanAxisUnit = this.translate.instant('COMMON.BOOLEAN_DATA_UNIT');
    this.booleanAxisCategories = [this.translate.instant('COMMON.FALSE'), this.translate.instant('COMMON.TRUE')];
    this.dashboardService.isChartDrawerOpen = false;
    this.subscriptions = [
      this.eventBus.onEvent(EditChartEvent.NAME, (event: EditChartEvent) => {
        this.dashboardService.isChartDrawerOpen = !this.dashboardService.isChartDrawerOpen;
        if (!this.dashboardService.isChartDrawerOpen) {
          this.drawerComponent?.instance.repaint();
        }
      }),
      this.eventBus.onEvent(CreateMetricGroupEvent.NAME, (event: CreateMetricGroupEvent) => {
        this.isPopupOpen = true;
      }),
      this.eventBus.onEvent(ReloadDataEvent.NAME, async (event: ReloadDataEvent) => {
        this.loadingChartInfo = true;
        this.loadData();
      }),
      this.eventBus.onEvent(DeleteChartEvent.NAME, (event: DeleteChartEvent) => {
        this.loadingChartInfo = true;
        this.deleteMetricGroup(event.value);
      }),
      // the impersonated user changed in the topbar: reload the list for the new one, then the
      // usual "pick the first chart and show its data" flow takes over. A widget always renders the
      // chart it was given, so it has nothing to switch
      this.eventBus.onEvent(ChangeImpersonatedUserEvent.NAME, (event: ChangeImpersonatedUserEvent) => {
        if (this.isWidget)
          return;
        this.loadingChartInfo = true;
        this.chartData = [];
        this.firstLoad();
      }),
      this.eventBus.onEvent(ExportChartEvent.NAME, (event: ExportChartEvent) => {
        if (!!this.chartComponent && !!this.userMetricGroup) {
          if (event.value === "csv") {
            this.parseAndDownloadChartDataForCsv()
          } else {
            this.chartComponent.instance.exportTo(this.userMetricGroup.name, event.value)
          }
        }
      })
    ]
  }

  ngAfterViewInit() {
    this.firstLoad()
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes.userMetricGroup.isFirstChange()) {
      if (!this.appService.deepEquals(changes.userMetricGroup.previousValue, changes.userMetricGroup.currentValue)) {
        this.loadingChartInfo = true;
        this.firstLoad();
      }
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    if (!!this.chartInterval) {                                               //
      clearInterval(this.chartInterval);                                      // If the chart was set to Auto-Refresh we call the
      this.chartInterval = undefined;                                         // getChartDataForSelectedChart at regular intervals
    }
  }

  setWidthOfDrawerPx(): number {
    if (this.appService.isMobileScreen()) {
      return document.documentElement.clientWidth - 20;
    } else {
      return document.documentElement.clientWidth * 0.8 - 20;
    }
  }

  /**
   * Async wrapper used to await the initialization of the metric groups and the chart data during the OnInit process
   */
  firstLoad() {
    // loaded once per charts page open, unlike the charts it doesn't change on save/delete. Only
    // administrators get the user selector, so nobody else has a use for the list
    if (!this.isWidget && this.dashboardService.canShareToOtherUsers())
      this.dashboardService.getUserImpersonationOptions().catch(() => {
      });

    this.dashboardService.getUserMetricGroups().then(() => {
      this.loadData()
    })
  }

  mapChartDatesInputForAPICall(cb: GetTimeChartMetricsCB) {
    if (!!this.userMetricGroup) {

      if (this.userMetricGroup.endDate === this.userMetricGroup.startDate) {
        this.userMetricGroup.endDate++;
      }

      if (this.userMetricGroup.selectedDateSettings === TypeOfDateSettingsEnumCharts.FIXED_DATE) {
        if (!!this.userMetricGroup.endDate) {
          cb.i.dateTo = new Date(this.userMetricGroup.endDate).getTime();
        }
        if (!!this.userMetricGroup.startDate) {
          cb.i.dateFrom = new Date(this.userMetricGroup.startDate).getTime();
        }
      } else if (this.userMetricGroup.selectedDateSettings === TypeOfDateSettingsEnumCharts.REALTIME) {
        let now = new Date();
        cb.i.dateTo = now.getTime();
        switch (this.userMetricGroup.realTimeUnit) {
          case TimeUnitEnum.MINUTES:
            cb.i.dateFrom = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), now.getMinutes() - this.userMetricGroup.realtimeValue, now.getSeconds()).getTime();
            break;
          case TimeUnitEnum.HOURS:
            cb.i.dateFrom = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours() - this.userMetricGroup.realtimeValue, now.getMinutes(), now.getSeconds()).getTime();
            break;
          case TimeUnitEnum.DAYS:
            cb.i.dateFrom = new Date(now.getFullYear(), now.getMonth(), now.getDate() - this.userMetricGroup.realtimeValue, now.getHours(), now.getMinutes(), now.getSeconds()).getTime();
            break;
          case TimeUnitEnum.WEEKS:
            cb.i.dateFrom = new Date(now.getFullYear(), now.getMonth(), now.getDate() - this.userMetricGroup.realtimeValue * 7, now.getHours(), now.getMinutes(), now.getSeconds()).getTime();
            break;
          case TimeUnitEnum.MONTHS:
            cb.i.dateFrom = new Date(now.getFullYear(), now.getMonth() - this.userMetricGroup.realtimeValue, now.getDate(), now.getHours(), now.getMinutes(), now.getSeconds()).getTime();
            break;
          case TimeUnitEnum.YEARS:
            cb.i.dateFrom = new Date(now.getFullYear() - this.userMetricGroup.realtimeValue, now.getMonth(), now.getDate(), now.getHours(), now.getMinutes(), now.getSeconds()).getTime();
            break;
        }
        this.userMetricGroup.endDate = new Date(now);
        this.userMetricGroup.startDate = new Date(cb.i.dateFrom);
      }

      if (!!this.userMetricGroup!.bucketValue)
        cb.i.bucketValue = this.userMetricGroup!.bucketValue;
      if (!!this.userMetricGroup!.bucketUnits)
        cb.i.bucketUnits = this.userMetricGroup!.bucketUnits;

    }
  }

  mapChartMetricsInputForAPICall(cb: GetTimeChartMetricsCB) {
    this.userMetricGroup?.metrics?.forEach((x) => {
      if (x.dataTypeGroup !== DataTypeGroupEnum.BOOLEAN) {
        let newMetric = new TimeChartRealMetricDTO();
        newMetric.decimalDigits = x.decimalDigits;
        newMetric.metricAlias = x.alias;
        newMetric.metricInstanceId = x.id;
        if (x.deviceId !== undefined)
          newMetric.deviceId = x.deviceId;
        newMetric.functionType = x.functionType;
        newMetric.isBoolean = false;
        cb.i.timeChartRealMetrics.push(newMetric)
      } else {
        let newMetric = new TimeChartRealMetricDTO();
        newMetric.metricAlias = x.alias;
        newMetric.metricInstanceId = x.id;
        newMetric.isBoolean = true;
        if (x.deviceId !== undefined)
          newMetric.deviceId = x.deviceId;
        cb.i.timeChartBooleanMetrics.push(newMetric);
      }
      let valueAxis = !!x.dataUnit ? x.dataUnit : " ";
      if (!this.valueAxisList.includes(valueAxis) && !(x.dataTypeGroup === DataTypeGroupEnum.BOOLEAN)) {
        this.valueAxisList.push(valueAxis);
      }
      if (x.dataTypeGroup === DataTypeGroupEnum.BOOLEAN && !this.valueAxisList.includes(this.translate.instant("COMMON.BOOLEAN_DATA_UNIT"))) {
        this.valueAxisList.push(this.translate.instant("COMMON.BOOLEAN_DATA_UNIT"));
      }
    });
  }

  mapChartDataFromAPIAnswer(cb: GetTimeChartMetricsCB) {
    let tempData: any[] = [];
    this.userMetricGroup!.metrics!.forEach(async (userMetricInstance) => {
      if (!!userMetricInstance.alias) {
        let metricData = cb.o.chartData.map(a => {
          let valToRet: any = {};
          let roundedTs = new Date(a["ts"]);
          roundedTs.setMilliseconds(0);
          valToRet['ts'] = roundedTs;
          if (a['hidden'] === true) valToRet['hidden'] = true;

          if (userMetricInstance.dataTypeGroup === DataTypeGroupEnum.FLOAT || userMetricInstance.dataTypeGroup === DataTypeGroupEnum.INTEGER) {
            if (a[userMetricInstance.alias!] !== undefined && a[userMetricInstance.alias!] !== null) {
              try {
                valToRet[userMetricInstance.alias!] = Number(a[userMetricInstance.alias!]);
              } catch (e: any) {
                valToRet[userMetricInstance.alias!] = a[userMetricInstance.alias!];
              }
            }

          } else if (userMetricInstance.dataTypeGroup === DataTypeGroupEnum.BOOLEAN) {
            if (a[userMetricInstance.alias!] !== undefined && a[userMetricInstance.alias!] !== null) {
              if (a[userMetricInstance.alias!])
                valToRet[userMetricInstance.alias!] = this.translate.instant("COMMON.TRUE");
              else
                valToRet[userMetricInstance.alias!] = this.translate.instant("COMMON.FALSE");
            }
          } else {
            valToRet[userMetricInstance.alias!] = a[userMetricInstance.alias!];
          }
          return valToRet;
        });
        tempData.push(metricData.filter(value => {
            // return index === 0 || index === array.length - 1 || value[x.alias!] !== null || array[index - 1][x.alias!] !== null
            return value[userMetricInstance.alias!] !== null && value[userMetricInstance.alias!] !== undefined
          })
        );
      }
    });
    this.chartData = tempData.flat();
  }

  startChartScheduledPolling() {
    if (!!this.chartInterval) {                                               //
      clearInterval(this.chartInterval);                                      // If the chart was set to Auto-Refresh we call the
      this.chartInterval = undefined;                                         // getChartDataForSelectedChart at regular intervals
    }
    let pollingFrequency;
    switch (this.userMetricGroup!.bucketUnits) {
      case BucketUnitEnum.SECONDS:
        pollingFrequency = this.userMetricGroup!.bucketValue * 1000;
        if (pollingFrequency < 5 * 60 * 1000)
          pollingFrequency = 5 * 60 * 1000;
        break;
      case BucketUnitEnum.MINUTES:
        if (this.userMetricGroup!.bucketValue > 5)
          pollingFrequency = this.userMetricGroup!.bucketValue * 60 * 1000;
        else
          pollingFrequency = 5 * 60 * 1000;
        break;
      case BucketUnitEnum.HOURS:
        pollingFrequency = this.userMetricGroup!.bucketValue * 60 * 60 * 1000;
        break;
      case BucketUnitEnum.DAYS:
      case BucketUnitEnum.MONTHS:
      case BucketUnitEnum.YEARS:
        pollingFrequency = 24 * 60 * 60 * 1000; // 1 Day (24 h) is the max polling time
        break;
      default:
        pollingFrequency = 0;
        break;
    }// in order to update the data of the chart

    if (pollingFrequency > 0) {
      this.chartInterval = setInterval(() => {
        if (!this.isDrawerOpen) {
          this.loadingChartInfo = true;
          this.callAPIForChartData()
            .finally(
              () => {
                this.loadingChartInfo = false;
                this.resetZoom(this.chartComponent);
              }
            )
        }
      }, pollingFrequency)
    }
  }

  loadSelectedChartDataAndStartPolling() {
    this.callAPIForChartData()
      .then(
        () => {
          this.startChartScheduledPolling();
        }
      )
      .finally(
        () => {
          this.resetZoom(this.chartComponent);
          this.loadingChartInfo = false;
        });
  }

  callAPIForChartData() {
    return new Promise(
      (resolve, reject) => {
        if (!!this.userMetricGroup) {

          let cb = new GetTimeChartMetricsCB();

          this.mapChartDatesInputForAPICall(cb);

          this.mapChartMetricsInputForAPICall(cb);

          this.telemetryService.getTimeChartMetrics(cb).then(
            () => {
              if (!!cb.o.warningMessage)
                this.toastService.openWarningToastMessage(cb.o.warningMessage);
              this.mapChartDataFromAPIAnswer(cb);
              resolve(true);
            }).catch(
            (error: any) => {
              reject(error);
            }
          );
        } else {
          reject(false);
        }
      });

  }

  /**
   * Initializes all the data related to the selected UserMetricGroup
   *
   * 1. Resets certain chart related variables
   * 2. Retrieves the detailed information of the group
   * 3. Maps the group in a format that will be easier to use later on. It also generates a unique alias for each metric
   * 4. Retrieves the telemetry data of the requested metric group in the selected time interval and saves it in the 'chartData' array
   */
  public async loadData() {
    this.loadingChartInfo = true;
    this.chartData = [];
    this.valueAxisList = [];
    if (!!this.drawerComponent)
      this.drawerComponent.instance.hide();

    if (!!this.resetZoomButton)
      this.resetZoomButton.visible = false;

    if (!this.dashboardService.selUserMetricGroup && !!this.dashboardService.userMetricGroups && this.dashboardService.userMetricGroups.length > 0)
      this.dashboardService.selUserMetricGroup = this.dashboardService.userMetricGroups[0];

    if (this.dashboardService.userMetricGroups.length === 0 && !this.isWidget) {
      this.dashboardService.selUserMetricGroup = null;
      this.userMetricGroup = undefined
    }

    if (!this.dashboardService.selUserMetricGroup) {
      this.chartData = [];
      this.loadingChartInfo = false;
      return
    }

    if (!this.isWidget && !!this.dashboardService.selUserMetricGroup) {
      this.userMetricGroup = this.dashboardService.selUserMetricGroup;

      if (this.userMetricGroup.endDate === this.userMetricGroup.startDate) {
        this.userMetricGroup.endDate++;
      }

      // the All variant covers the caller's own charts too, getUserMetricGroup is self-only and
      // would 403 on an impersonated one
      let umgCB = new GetAllUserMetricGroupCB();
      umgCB.i.id = this.userMetricGroup.id;
      this.userService.getAllUserMetricGroup(umgCB).then(
        () => {
          this.userMetricGroup = new UserMetricGroup(umgCB);
          this.dashboardService.fullSelUserMetricGroup = new UserMetricGroup(umgCB);
          this.loadSelectedChartDataAndStartPolling();
        }).catch(
        (error: any) => {
          this.toastService.openErrorToastMessage(error);
          this.loadingChartInfo = false;
        })
    } else {
      this.loadSelectedChartDataAndStartPolling();
    }

  }

  /**
   * Writes the text shown in the hover tooltip.
   * Shows the time of the point as a header, then shows the data and a summary of the stack.
   *
   * @param info Object containing information about the part of the chart that is currently
   * being hovered
   */
  composeChartTooltip(info: any) {
    const argTime = new Date(info.argument).getTime();
    if (this._tooltipCache?.arg === argTime) return this._tooltipCache.html;

    let theXValFormatted: string;

    const theXVal = new Date(info.argument);
    // format the  theXVal Date in the format dd/MM/yyyy hh:mm:ss

    const dateDay = ('0' + theXVal.getDate()).slice(-2);
    const dateMonth = ('0' + (theXVal.getMonth() + 1)).slice(-2);
    const dateYear = theXVal.getFullYear();
    const dateHour = ('0' + theXVal.getHours()).slice(-2);
    const dateMinute = ('0' + theXVal.getMinutes()).slice(-2);
    const dateSecond = ('0' + theXVal.getSeconds()).slice(-2);


    if (this.userMetricGroup?.bucketUnits === BucketUnitEnum.DAYS || this.userMetricGroup?.bucketUnits === BucketUnitEnum.MONTHS || this.userMetricGroup?.bucketUnits === BucketUnitEnum.YEARS) {
      theXValFormatted = `${dateDay}/${dateMonth}/${dateYear}`;
    } else {
      theXValFormatted = `${dateHour}:${dateMinute}:${dateSecond} - ${dateDay}/${dateMonth}/${dateYear}`;
    }

    let tooltip = "";

    info.points.filter(
      (obj: any, index: number, self: any[]) =>
        index === self.findIndex((t) => t.point.series.tag.alias === obj.point.series.tag.alias)
    ).forEach((p: any) => {
      let fullSeries: UserMetricInstance = p.point.series.tag;
      if (!!fullSeries) {

        let metricOrigin: string;
        let metricValue: string = "";

        if (!!fullSeries.deviceName) {
          metricOrigin = `<span>${fullSeries.partitionName} > </span> <span>${fullSeries.deviceName} > </span> <span>${fullSeries.name}</span>`;
        } else {
          metricOrigin = `<span>${fullSeries.partitionName} > </span> <span>${this.translate.instant("CHART_CREATION.VIRTUAL_METRICS")} > </span> <span>${fullSeries.name}</span>`
        }

        if (fullSeries.dataTypeGroup === DataTypeGroupEnum.INTEGER || fullSeries.dataTypeGroup === DataTypeGroupEnum.FLOAT) {

          if (fullSeries.dataTypeGroupShowAsHex) {
            if (p.value !== undefined && p.value !== null) {
              metricValue = '0x' + Number(p.value).toString(16).toUpperCase();
            }
          } else {
            metricValue = `${Number(p.value).toFixed(fullSeries.decimalDigits?? 0)}`;
          }
        } else {
          metricValue = `${p.value}`;
        }

        tooltip += `<span style="${p.point.series.tag === info.point.series.tag ? 'font-weight: bold' : ''}"> <span style="color:${p.point.getColor()}; font-size: 10px;" class="material-icons">circle</span> ${metricOrigin}: <span><b>${metricValue}</b></span><span> ${fullSeries.dataUnit ? fullSeries.dataUnit : ''} </span> </span>`;

      }
    });
    if (!!tooltip) {
      tooltip = `<div style="width: 100%; display: flex; justify-content: center"><b>${theXValFormatted}</b></div><br/><div style="width: 100%; max-width:75dvw; display: flex; flex-wrap: wrap; flex-direction: column;">${tooltip}</div>`;
    }

    const result = this.sanitizer.bypassSecurityTrustHtml(tooltip);
    this._tooltipCache = { arg: argTime, html: result };
    return result;
  }

  onRangeSelectorValueChanged(e: any) {
    const newStart = (e.value[0] as Date)?.getTime();
    const newEnd = (e.value[1] as Date)?.getTime();
    const curStart = (this.visualRangeX as Date[])?.[0]?.getTime();
    const curEnd = (this.visualRangeX as Date[])?.[1]?.getTime();
    if (newStart !== curStart || newEnd !== curEnd) {
      this.visualRangeX = e.value;
    }
  }

  hideOutsideTooltip(e: any) {
    const ts = new Date(e.target.argument).getTime();
    const start = new Date(this.userMetricGroup!.startDate).getTime();
    const end = new Date(this.userMetricGroup!.endDate).getTime();
    if (ts < start || ts > end || e.target.data?.hidden === true) {
      e.component.hideTooltip();
    }
  }

  customizeOutsidePoint = (pointInfo: any) => {
    const ts = new Date(pointInfo.argument).getTime();
    const start = new Date(this.userMetricGroup!.startDate).getTime();
    const end = new Date(this.userMetricGroup!.endDate).getTime();
    if (ts < start || ts > end || pointInfo.data?.hidden === true) {
      return { hoverStyle: { size: 0 }, color: 'transparent' };
    }
    return {};
  }

  /**
   * function used by the series *ngFor loop to optimize it
   * @param index
   * @param item
   */
  trackByFn(index: any, item: any) {
    return item.id;
  }

  /**
   * Handles the chart zoom. In details:
   * - Check whether the chart is still zoomed or the user manually reset it
   * - Synchronizes the zoom-by-selection and the zoom-by-slider
   * - Shows or hides the reset zoom FAB button
   * @param $event
   */
  handleZoom($event: ZoomEndEvent) {
    if (!!this.chartSliderComponent && !!this.resetZoomButton && !!this.userMetricGroup) {
      if (typeof $event.range.startValue === "object" && typeof $event.range.endValue === "object") {
        this.chartIsZoomedX = true;
        if (!!$event.actionType)
          this.chartSliderComponent.instance.setValue([$event.range.startValue, $event.range.endValue]);
        if (new Date(this.userMetricGroup.endDate).getTime() === ($event.range.endValue as Date).getTime() &&
          new Date(this.userMetricGroup.startDate).getTime() === ($event.range.startValue as Date).getTime()) {
          this.chartIsZoomedX = false;
        }
      } else {
        if ($event.actionType !== "pan") {
          this.chartIsZoomedY = $event.zoomFactor > 1;
        }
      }
      this.resetZoomButton.visible = this.chartIsZoomedX || this.chartIsZoomedY;
    }
  }


  /**
   * Resets the Visual Range to its original unzoomed state and resets both the 'chartIsZoomed' variables
   */
  resetZoom(chartComponent?: DxChartComponent) {
    if (!!this.chartData && !!this.userMetricGroup) {
      if (!!chartComponent && !!chartComponent.instance)
        chartComponent.instance.resetVisualRange();
      if (!!this.chartSliderComponent)
        this.chartSliderComponent.instance.setValue([new Date(this.userMetricGroup.startDate), new Date(this.userMetricGroup.endDate)]);
      this.visualRangeX = [new Date(this.userMetricGroup.startDate), new Date(this.userMetricGroup.endDate)];
    }
  }

  /**
   * Retrieves the metric color in case the chart automatically generated it in order to use it later.
   * @param $event
   */
  getMetricColor($event: any) {
    $event.component.getAllSeries().forEach((series: any) => {
      if (!!this.userMetricGroup && !!this.userMetricGroup.metrics) {
        let seriesToChange = this.userMetricGroup.metrics.find(s => {
          return s.alias === series.tag
        });
        if (!!seriesToChange && !seriesToChange.color && seriesToChange.color !== series.getColor()) {
          seriesToChange.color = series.getColor();
        }
      }
    })
  }

  /**
   * Prepares the CB and calls the putUserMetricGroup API. After that it automatically selects the newly created userMetricGroup
   */
  async createNewMetricGroup(newUserMetricGroup?: UserMetricGroup) {
    if (!!newUserMetricGroup) {
      this.loadingChartInfo = true;
      let cb = new PutUserMetricGroupCB();
      cb.i.name = newUserMetricGroup.name;
      cb.i.metrics = [];
      cb.i.bucketUnits = !!newUserMetricGroup.bucketUnits ? newUserMetricGroup.bucketUnits : BucketUnitEnum.HOURS;
      cb.i.bucketValue = !!newUserMetricGroup.bucketValue ? newUserMetricGroup.bucketValue : 1;
      this.userOpeService.putUserMetricGroup(cb).then(
        () => {
          this.isPopupOpen = false;
          this.dashboardService.getUserMetricGroups().then(
            () => {
              let theNewGroup = this.dashboardService.userMetricGroups.find(g => g.id === cb.o.id);
              if (!!theNewGroup)
                this.dashboardService.selUserMetricGroup = theNewGroup;
              else
                this.dashboardService.selUserMetricGroup = null;
              this.loadData().then(
                () => {
                  if (!!this.dashboardService.selUserMetricGroup) {
                    this.userMetricGroup = this.dashboardService.selUserMetricGroup;
                    if (!!this.drawerComponent)
                      this.drawerComponent.instance.show();
                    this.toastService.openSuccessToastMessage(this.translate.instant("CHART_CREATION.USER_METRIC_GROUP_CREATED"))
                  }
                });
            });
        }).catch((error: any) => {
          this.loadingChartInfo = false;
          this.createRequestSent = false;
          this.toastService.openErrorToastMessage(error);
        }
      )
    } else {
      this.isPopupOpen = false;
    }
  }

  /**
   * If the user wants to save his changes this method calls the putUserMetricGroup API to persist the changes on the database.
   * after doing that it calls the getUserMetricGroups method to update the list of the UserMetricGroups
   *
   * If the user wants to discard his changes it reverts all the changed data back to its starting point.
   *
   * @param cb Contains the data to save if the user wants to save, Contains false if the user wants to discard changes
   */
  async saveChanges(cb: PutUserMetricGroupCB | false) {
    this.loadingChartInfo = true;
    if (!!cb) {
      try {
        this.userService.putUserMetricGroup(cb).then(
          () => {
            this.dashboardService.getUserMetricGroups().then(
              () => {
                this.loadData().then(
                  () => {
                    this.toastService.openSuccessToastMessage(this.translate.instant("CHART_CREATION.USER_METRIC_GROUP_UPDATED"))
                  })
              })
          }).catch(
          (error: any) => {
            this.loadingChartInfo = false;
            this.toastService.openErrorToastMessage(error);
          })
      } catch (error: any) {
        this.loadingChartInfo = false;
        this.toastService.openErrorToastMessage(error);
      }
    }
  }

  /**
   * Permanently deletes the metric group by calling the delUserMetricGroup API and then reloads.
   * @param id
   */
  deleteMetricGroup(id: number) {

    let cb = new DelUserMetricGroupCB();
    cb.i.id = id;
    cb.i.userId = this.gmService.user.id;
    this.userService.delUserMetricGroup(cb).then(
      () => {
        this.dashboardService.getUserMetricGroups().then(
          () => {
            this.loadData();
          });
      });

  }

  loadDrawerInfo(isOpening: boolean) {
    if (isOpening) {
      this.formDrawerComponent?.loadFormData(this.userMetricGroup);
    }
  }

  getSeriesName(metric: UserMetricInstance) {
    if (!!metric.name) {
      let name = metric.name;
      if (!!metric.dataUnit)
        name += ' [' + metric.dataUnit + ']';
      if (!!metric.partitionName)
        name += ' [' + metric.partitionName + ']';
      if (!!metric.deviceName)
        name += ' [' + metric.deviceName + ']';
      return name;
    }
    return "";
  }

  getParsedColumnTitleForCsv(alias: string) {
    if (!!this.userMetricGroup && !!this.userMetricGroup.metrics) {
      const fullMetric = this.userMetricGroup.metrics.find(metric => {
        return metric.alias === alias
      });
      if (!!fullMetric) {
        return this.getSeriesName(fullMetric)
      }
    }
    if (alias === "ts") {
      return "Time";
    }
    return alias;
  }

  public parseAndDownloadChartDataForCsv() {

    // Data returned from the BE is messy we need to clean it.

    let cleanedChartData: any[] = [];

    // Using the ts as key we will create a new array of object unifying all
    // entries under the same timestamp

    this.chartData.forEach((data: any) => {
      let maybeIndex = cleanedChartData.findIndex(x => {
        const xDate = new Date(x.ts);
        const dataDate = new Date(data.ts);
        xDate.setSeconds(0);
        dataDate.setSeconds(0);
        return xDate.getTime() === dataDate.getTime();
      });
      if (maybeIndex === -1) {
        if (this.hasNonNullProperty(data))
          maybeIndex = cleanedChartData.length;
        cleanedChartData.push({});
      }
      for (let entry in data) {
        if (data[entry] !== undefined && data[entry] !== null)
          cleanedChartData[maybeIndex][entry] = data[entry];
        if (!!this.userMetricGroup && !!this.userMetricGroup.metrics) {
          const dataSeries = this.userMetricGroup.metrics.find(metric => metric.alias === entry);
          if (!!dataSeries) {
            if (dataSeries.dataTypeGroup === DataTypeGroupEnum.BOOLEAN) {
              cleanedChartData[maybeIndex][entry] = data[entry] === this.translate.instant("COMMON.TRUE");
            }
          }
        }

      }
    });

    // Then we take the data and download it as a csv file

    this.downloadCSV(cleanedChartData.sort((a, b) => a.ts - b.ts));

  }

  downloadCSV(arrayOfObjects: any, filename = 'data.csv', sep = ',') {
    // Get all unique headers (keys) from all objects in the array
    const rawHeaders: string[] = Array.from(new Set(arrayOfObjects.flatMap((obj: any) => Object.keys(obj))));

    // Map raw headers to parsed headers
    const headers = rawHeaders.map(rawName => this.getParsedColumnTitleForCsv(rawName));
    // Map each object to a CSV row
    const rows = arrayOfObjects.map((obj: any) =>
      rawHeaders.map((header, index) => {
        // The first column will be parsed as a timestamp
        const value = obj[header];

        // Parse the timestamp if it is in the first column
        let parsedValue;
        if (index === 0 && (value !== null && value !== undefined))
          parsedValue = this.datePipe.transform(value, 'dd/MM/yyyy HH:mm:ss');
        else if (value === null || value === undefined)
          parsedValue = undefined;
        else if (typeof value === "boolean")
          parsedValue = value ? 1 : 0;
        else
          parsedValue = JSON.stringify(value);

        return parsedValue;
      }).join(sep)
    );

    // Join headers and rows together
    const csvContent = ['sep=' + sep, headers.join(sep), ...rows].join('\n');

    // Create a Blob from the CSV content
    const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'});

    // Create a link element to trigger file download
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;

    // Programmatically click the link to trigger the download
    link.click();
  }

  hasNonNullProperty(obj: any) {
    return Object.values(obj).some(value => value !== null && value !== undefined);
  }

  onLegendClick(e: any) {
    e.target.isVisible() ? e.target.hide() : e.target.show();

    if (this.userMetricGroup?.metrics) {
      const clickerMetric = this.userMetricGroup.metrics.find(m => m.id === e.target.tag.id);
      if (clickerMetric) {
        clickerMetric.isVisible = e.target.isVisible();
      }
    }
    this.updateHiddenAxes();
    this.cdr.detectChanges();
  }

  private updateHiddenAxes() {
    const next = new Set<string>();
    for (const axisName of this.valueAxisList) {
      const axisMetrics = this.userMetricGroup?.metrics?.filter(
        m => this.getSeriesAxis(m) === axisName
      ) ?? [];
      const allHidden = axisMetrics.length > 0 && axisMetrics.every(m => {
        const s = this.chartComponent?.instance.getSeriesByName(this.getSeriesName(m));
        return s && !s.isVisible();
      });
      if (allHidden) next.add(axisName);
    }
    this.hiddenAxes = next;
  }

  getSeriesAxis(metric: UserMetricInstance) {
    if (metric.dataTypeGroup === DataTypeGroupEnum.BOOLEAN)
      return this.booleanAxisUnit;
    else if (!metric.dataUnit)
      return " ";
    else
      return metric.dataUnit
  }
}
